import React, { useState, useEffect } from "react";
import {
  getBlueprints, getActiveBlueprint, createBlueprint,
  activateBlueprint, deactivateBlueprint, deleteBlueprint,
} from "../api";
import { C, LAYOUT } from "../constants";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Badge from "../components/Badge";

const fieldStyle = {
  width: "100%",
  padding: "8px 10px",
  border: `1.5px solid ${C.border}`,
  borderRadius: LAYOUT.radiusSm,
  fontSize: 13,
  boxSizing: "border-box",
  fontFamily: "inherit",
  background: C.surface,
  color: C.text,
};

function PartInput({ label, value, onChange }) {
  return (
    <div style={{
      background: C.surfaceAlt,
      border: `1px solid ${C.border}`,
      borderRadius: LAYOUT.radiusSm,
      padding: 14,
      marginBottom: 10,
    }}>
      <div style={{ fontWeight: 800, fontSize: 13, color: C.text, marginBottom: 10 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {[["count", "Count"], ["type", "Type"], ["marks", "Marks each"]].map(([key, lbl]) => (
          <div key={key}>
            <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 2 }}>{lbl}</label>
            <input
              type={key === "type" ? "text" : "number"}
              value={value[key] || ""}
              onChange={(e) => onChange({
                ...value,
                [key]: key === "type" ? e.target.value : Number(e.target.value),
              })}
              style={fieldStyle}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BlueprintManager() {
  const [blueprints, setBlueprints] = useState([]);
  const [active, setActive] = useState(null);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [partA, setPartA] = useState({ count: 20, type: "MCQ", marks: 1 });
  const [partB, setPartB] = useState({ count: 5, type: "Short Answer", marks: 5 });
  const [partC, setPartC] = useState({ count: 2, type: "Essay", marks: 10 });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    Promise.all([getBlueprints(), getActiveBlueprint()]).then(([r1, r2]) => {
      if (r1.success) setBlueprints(r1.blueprints || []);
      if (r2.success) setActive(r2.blueprint);
    }).catch(() => {});
  }, []);

  const handleCreate = async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await createBlueprint({ name: name || undefined, partA, partB, partC, notes });
      if (res.success) {
        setMsg({ type: "success", text: res.message });
        const r = await getBlueprints();
        if (r.success) setBlueprints(r.blueprints);
        setActive(res.blueprint);
        setName("");
        setNotes("");
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.error || "Failed" });
    }
    setLoading(false);
  };

  const handleActivate = async (v) => {
    try {
      const res = await activateBlueprint(v);
      if (res.success) {
        setActive(res.blueprint);
        setBlueprints((prev) => prev.map((b) => ({ ...b, isActive: b.version === v })));
        setMsg({ type: "success", text: `Blueprint v${v} is now active` });
      }
    } catch {
      setMsg({ type: "error", text: "Activation failed" });
    }
  };

  const handleDeactivate = async (v) => {
    try {
      const res = await deactivateBlueprint(v);
      if (res.success) {
        setActive(null);
        setBlueprints((prev) => prev.map((b) => ({ ...b, isActive: false })));
        setMsg({ type: "success", text: res.message });
      }
    } catch {
      setMsg({ type: "error", text: "Deactivation failed" });
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete Blueprint v${v}? This cannot be undone.`)) return;
    try {
      const res = await deleteBlueprint(v);
      if (res.success) {
        setBlueprints((prev) => prev.filter((b) => b.version !== v));
        if (active?.version === v) setActive(null);
        setMsg({ type: "success", text: res.message });
      }
    } catch {
      setMsg({ type: "error", text: "Delete failed" });
    }
  };

  const totalMarks = (p) =>
    (p.partA.count * p.partA.marks) + (p.partB.count * p.partB.marks) + (p.partC.count * p.partC.marks);

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "8px 4px 28px" }}>
      <PageHeader
        title="Blueprint Manager"
        subtitle="Manage exam patterns. When the university changes the blueprint, create a new version — assessments and worksheets can be regenerated."
        icon="📐"
      />

      {msg && (
        <div style={{
          padding: "10px 14px",
          borderRadius: LAYOUT.radiusSm,
          marginBottom: 16,
          fontSize: 13,
          fontWeight: 600,
          background: msg.type === "error" ? C.errorBg : C.successBg,
          color: msg.type === "error" ? C.error : C.success,
          border: `1px solid ${msg.type === "error" ? `${C.error}33` : `${C.success}33`}`,
        }}>
          {msg.text}
        </div>
      )}

      {active && (
        <Card
          style={{ marginBottom: 16 }}
          sx={{
            background: `${C.successBg} !important`,
            border: `1px solid ${C.success}44 !important`,
            borderLeft: `4px solid ${C.success} !important`,
          }}
        >
          <div style={{ fontWeight: 800, color: C.success, marginBottom: 6 }}>
            Active — {active.name} (v{active.version})
          </div>
          <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>
            Part A: {active.pattern?.partA?.count} {active.pattern?.partA?.type} ({active.pattern?.partA?.marks}m) ·
            Part B: {active.pattern?.partB?.count} {active.pattern?.partB?.type} ({active.pattern?.partB?.marks}m) ·
            Part C: {active.pattern?.partC?.count} {active.pattern?.partC?.type} ({active.pattern?.partC?.marks}m)
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
            Total: {totalMarks(active.pattern)} marks
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 14px", color: C.text }}>
          Create new blueprint version
        </h3>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.muted, display: "block", marginBottom: 4 }}>
            Blueprint name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Semester V Pattern"
            style={{ ...fieldStyle, padding: "10px 12px", fontSize: 14 }}
          />
        </div>
        <PartInput label="Part A" value={partA} onChange={setPartA} />
        <PartInput label="Part B" value={partB} onChange={setPartB} />
        <PartInput label="Part C" value={partC} onChange={setPartC} />
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
          Total marks: {(partA.count * partA.marks) + (partB.count * partB.marks) + (partC.count * partC.marks)}
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.muted, display: "block", marginBottom: 4 }}>
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reason for change, effective from…"
            rows={2}
            style={{ ...fieldStyle, padding: "10px 12px", resize: "vertical" }}
          />
        </div>
        <Btn onClick={handleCreate} disabled={loading}>
          {loading ? "Saving…" : "Save as new version"}
        </Btn>
      </Card>

      <Card style={{ padding: 0 }} sx={{ p: "0 !important", overflow: "hidden" }}>
        <div style={{
          padding: "14px 16px",
          borderBottom: `1px solid ${C.border}`,
          fontWeight: 800,
          fontSize: 14,
          color: C.text,
        }}>
          Blueprint history ({blueprints.length})
        </div>
        {blueprints.length === 0 ? (
          <p style={{ padding: 16, color: C.muted, fontSize: 13 }}>No blueprints yet.</p>
        ) : (
          blueprints.map((b, idx) => (
            <div
              key={b.version}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                borderBottom: idx < blueprints.length - 1 ? `1px solid ${C.border}` : "none",
                background: b.isActive ? C.successBg : C.surface,
              }}
            >
              <div style={{ fontWeight: 800, fontSize: 15, color: b.isActive ? C.success : C.text, minWidth: 30 }}>
                v{b.version}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{b.name}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                  A:{b.pattern?.partA?.count} {b.pattern?.partA?.type} ·
                  B:{b.pattern?.partB?.count} {b.pattern?.partB?.type} ·
                  C:{b.pattern?.partC?.count} {b.pattern?.partC?.type}
                </div>
              </div>
              {b.isActive ? (
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <Badge bg={C.successBg} color={C.success}>ACTIVE</Badge>
                  <Btn
                    small
                    variant="outline"
                    onClick={() => handleDeactivate(b.version)}
                    style={{ borderColor: `${C.warning}66`, color: C.warning, background: C.warningBg }}
                  >
                    Deactivate
                  </Btn>
                  <Btn small variant="ghost" onClick={() => handleDelete(b.version)} style={{ color: C.error }}>
                    Delete
                  </Btn>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 6 }}>
                  <Btn small variant="outline" onClick={() => handleActivate(b.version)}>
                    Set active
                  </Btn>
                  <Btn small variant="ghost" onClick={() => handleDelete(b.version)} style={{ color: C.error }}>
                    Delete
                  </Btn>
                </div>
              )}
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
